import React, { useState } from 'react';
import {
  Dialog,
  Button,
  Typography,
  Box,
  CircularProgress,
  Chip,
  Alert,
  IconButton,
  Tabs,
  Tab,
  Paper,
  DialogTitle,
} from '@mui/material';
import { Build as BuildIcon, CheckCircle as CheckCircleIcon, Close as CloseIcon } from '@mui/icons-material';
import Draggable from 'react-draggable';
import { jerseyApi } from '../../api/jerseyApi';

interface PatternTransformMismatch {
  element_id: string;
  pattern_slug: string;
  pattern_id?: number | null;
  pattern_group_id: string;
  library_transform: string;
  parent_transform?: string | null;
  jersey_transform: string;
  missing_transform?: string | null;
}

interface PatternTransformFixProps {
  open: boolean;
  onClose: () => void;
  frontSvg: string | null;
  sideSvg: string | null;
  onSvgUpdate: (frontSvg: string, sideSvg: string) => void;
}

// Helper function to simplify transform display
const simplifyTransform = (transform: string): string => {
  if (!transform) return '';

  // Extract translate values
  const translateMatch = transform.match(/translate\(([^)]+)\)/);
  if (translateMatch) {
    const values = translateMatch[1].split(',').map(v => v.trim());
    if (values.length === 2) {
      return `v1: ${values[0]}, v2: ${values[1]}`;
    } else if (values.length === 1) {
      const parts = values[0].split(/\s+/);
      if (parts.length === 2) {
        return `v1: ${parts[0]}, v2: ${parts[1]}`;
      }
    }
  }

  // Fallback: just show first 30 chars
  return transform.substring(0, 30) + (transform.length > 30 ? '...' : '');
};

// Draggable Paper Component
function DraggablePaper(props: any) {
  return (
    <Draggable
      handle="#draggable-dialog-title"
      cancel={'[class*="MuiDialogContent-root"]'}
    >
      <Paper {...props} />
    </Draggable>
  );
}

const PatternTransformFix: React.FC<PatternTransformFixProps> = ({
  open,
  onClose,
  frontSvg,
  sideSvg,
  onSvgUpdate,
}) => {
  const [loading, setLoading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [frontMismatches, setFrontMismatches] = useState<PatternTransformMismatch[]>([]);
  const [sideMismatches, setSideMismatches] = useState<PatternTransformMismatch[]>([]);
  const [appliedFixes, setAppliedFixes] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);
  const [selectedIndex, setSelectedIndex] = useState(0);

  const analyzeMismatches = async () => {
    if (!frontSvg && !sideSvg) {
      setError('No SVG available to analyze');
      return;
    }

    setAnalyzing(true);
    setError(null);
    setFrontMismatches([]);
    setSideMismatches([]);

    try {
      // Analyze front SVG
      if (frontSvg) {
        const frontResult = await jerseyApi.analyzePatternTransforms({
          svg: frontSvg,
          view: 'front'
        });
        setFrontMismatches(frontResult.mismatches);
        console.log('Front mismatches:', frontResult.mismatches);
      }

      // Analyze side SVG
      if (sideSvg) {
        const sideResult = await jerseyApi.analyzePatternTransforms({
          svg: sideSvg,
          view: 'side'
        });
        setSideMismatches(sideResult.mismatches);
        console.log('Side mismatches:', sideResult.mismatches);
      }
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Failed to analyze pattern transforms');
      console.error('Error analyzing transforms:', err);
    } finally {
      setAnalyzing(false);
    }
  };

  const applyFix = async (
    mismatch: PatternTransformMismatch,
    view: 'front' | 'side',
    transformType: 'pattern' | 'parent' = 'pattern'
  ) => {
    setLoading(true);
    setError(null);

    try {
      const currentSvg = view === 'front' ? frontSvg : sideSvg;
      if (!currentSvg) return;

      // Choose which transform to apply
      const transform = transformType === 'parent'
        ? mismatch.parent_transform
        : mismatch.library_transform;

      if (!transform) {
        setError('No transform available');
        setLoading(false);
        return;
      }

      const result = await jerseyApi.applyPatternTransform({
        svg: currentSvg,
        pattern_group_id: mismatch.pattern_group_id,
        transform,
      });

      if (result.success) {
        // Update the appropriate SVG
        if (view === 'front') {
          onSvgUpdate(result.svg, sideSvg || '');
        } else {
          onSvgUpdate(frontSvg || '', result.svg);
        }

        // Mark as applied
        const fixKey = `${view}-${mismatch.pattern_group_id}`;
        setAppliedFixes(prev => new Set([...prev, fixKey]));

        // Remove from mismatches list
        if (view === 'front') {
          setFrontMismatches(prev => prev.filter(m => m.pattern_group_id !== mismatch.pattern_group_id));
        } else {
          setSideMismatches(prev => prev.filter(m => m.pattern_group_id !== mismatch.pattern_group_id));
        }
      }
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Failed to apply transform fix');
      console.error('Error applying fix:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleOpen = () => {
    setAppliedFixes(new Set());
    analyzeMismatches();
  };

  React.useEffect(() => {
    if (open) {
      handleOpen();
    }
  }, [open]);

  // Combine all mismatches into a single array with view info
  const allMismatches = [
    ...frontMismatches.map(m => ({ ...m, view: 'front' as const })),
    ...sideMismatches.map(m => ({ ...m, view: 'side' as const }))
  ];

  const totalMismatches = allMismatches.length;
  const currentMismatch = allMismatches[selectedIndex];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperComponent={DraggablePaper}
      aria-labelledby="draggable-dialog-title"
      PaperProps={{
        sx: {
          position: 'fixed',
          top: '20%',
          left: '50%',
          transform: 'translate(-50%, 0)',
          m: 0,
          maxHeight: '60vh',
        }
      }}
    >
      {/* Draggable Header */}
      <DialogTitle
        id="draggable-dialog-title"
        sx={{
          cursor: 'move',
          p: 2,
          pb: 1,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          userSelect: 'none',
        }}
      >
        <Box display="flex" alignItems="center" gap={1}>
          <BuildIcon color="primary" />
          <Typography variant="h6">Fix Pattern Transforms</Typography>
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <Box sx={{ px: 2, pb: 2 }}>
        {analyzing && (
          <Box display="flex" justifyContent="center" alignItems="center" py={4}>
            <CircularProgress size={24} />
            <Typography variant="body2" sx={{ ml: 2 }}>
              Analyzing...
            </Typography>
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {!analyzing && totalMismatches === 0 && (
          <Alert severity="success" icon={<CheckCircleIcon />}>
            No transform issues found!
          </Alert>
        )}

        {!analyzing && totalMismatches > 0 && (
          <Box>
            {/* Navigation Tabs */}
            <Tabs
              value={selectedIndex}
              onChange={(_, newValue) => setSelectedIndex(newValue)}
              variant="scrollable"
              scrollButtons="auto"
              sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
            >
              {allMismatches.map((mismatch, index) => {
                const fixKey = `${mismatch.view}-${mismatch.pattern_group_id}`;
                const isApplied = appliedFixes.has(fixKey);
                return (
                  <Tab
                    key={index}
                    label={
                      <Box display="flex" alignItems="center" gap={0.5}>
                        <Typography variant="caption">
                          {mismatch.element_id}
                        </Typography>
                        {isApplied && <CheckCircleIcon sx={{ fontSize: 14 }} color="success" />}
                      </Box>
                    }
                  />
                );
              })}
            </Tabs>

            {/* Current Mismatch Details */}
            {currentMismatch && (() => {
              const fixKey = `${currentMismatch.view}-${currentMismatch.pattern_group_id}`;
              const isApplied = appliedFixes.has(fixKey);

              return (
                <Paper elevation={0} sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
                  {/* Element Info */}
                  <Box mb={2}>
                    <Typography variant="h6" gutterBottom>
                      {currentMismatch.element_id}
                    </Typography>
                    <Chip
                      label={currentMismatch.pattern_slug}
                      size="small"
                      color="primary"
                      variant="outlined"
                      sx={{ mr: 1 }}
                    />
                    <Chip
                      label={currentMismatch.view}
                      size="small"
                      variant="outlined"
                    />
                  </Box>

                  {/* Current Transform */}
                  {currentMismatch.jersey_transform && (
                    <Box mb={2}>
                      <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                        Current Transform:
                      </Typography>
                      <Typography variant="body2" fontFamily="monospace">
                        {simplifyTransform(currentMismatch.jersey_transform)}
                      </Typography>
                    </Box>
                  )}

                  {/* Status */}
                  {isApplied && (
                    <Alert severity="success" icon={<CheckCircleIcon />} sx={{ mb: 2 }}>
                      Transform applied
                    </Alert>
                  )}

                  {/* Transform Options */}
                  <Typography variant="subtitle2" gutterBottom>
                    Apply Transform:
                  </Typography>
                  <Box display="flex" flexDirection="column" gap={1.5}>
                    {currentMismatch.library_transform && (
                      <Button
                        variant="contained"
                        size="medium"
                        fullWidth
                        onClick={() => applyFix(currentMismatch, currentMismatch.view, 'pattern')}
                        disabled={loading || isApplied}
                        startIcon={<BuildIcon />}
                      >
                        <Box textAlign="left" width="100%">
                          <Typography variant="body2" fontWeight="bold">
                            Pattern Transform
                          </Typography>
                          <Typography variant="caption" display="block" sx={{ opacity: 0.8 }}>
                            {simplifyTransform(currentMismatch.library_transform)}
                          </Typography>
                        </Box>
                      </Button>
                    )}

                    {currentMismatch.parent_transform && (
                      <Button
                        variant="outlined"
                        size="medium"
                        fullWidth
                        onClick={() => applyFix(currentMismatch, currentMismatch.view, 'parent')}
                        disabled={loading || isApplied}
                        startIcon={<BuildIcon />}
                      >
                        <Box textAlign="left" width="100%">
                          <Typography variant="body2" fontWeight="bold">
                            Parent Transform
                          </Typography>
                          <Typography variant="caption" display="block" sx={{ opacity: 0.7 }}>
                            {simplifyTransform(currentMismatch.parent_transform)}
                          </Typography>
                        </Box>
                      </Button>
                    )}
                  </Box>
                </Paper>
              );
            })()}
          </Box>
        )}
      </Box>
    </Dialog>
  );
};

export default PatternTransformFix;

